/* ============================================================
   VOLTURA THEME — CORE JS
   ============================================================ */

'use strict';

const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

function debounce(fn, delay = 200) {
  let timer;
  return (...args) => { clearTimeout(timer); timer = setTimeout(() => fn(...args), delay); };
}

function formatMoney(cents) {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: window.Shopify?.currency?.active || 'EUR' }).format(cents / 100);
}

// ============================================================
// HEADER — SCROLL EFFECT
// ============================================================
(function initHeader() {
  const header = $('.site-header');
  if (!header) return;
  const onScroll = debounce(() => { header.classList.toggle('scrolled', window.scrollY > 20); }, 50);
  window.addEventListener('scroll', onScroll, { passive: true });
})();

// ============================================================
// ANNOUNCEMENT BAR — DISMISS
// ============================================================
(function initAnnouncementBar() {
  const bar = $('.announcement-bar');
  const closeBtn = $('.announcement-bar__close');
  if (!bar || !closeBtn) return;
  if (sessionStorage.getItem('announcement-dismissed')) { bar.style.display = 'none'; return; }
  closeBtn.addEventListener('click', () => { bar.style.display = 'none'; sessionStorage.setItem('announcement-dismissed', '1'); });
})();

// ============================================================
// MOBILE MENU
// ============================================================
(function initMobileMenu() {
  const toggleBtn = $('.header__mobile-menu-btn');
  const menu = $('.mobile-menu');
  const closeBtn = $('.mobile-menu__close');
  const overlay = $('#overlay');
  if (!toggleBtn || !menu) return;
  function openMenu() { menu.classList.add('open'); overlay?.classList.add('active'); document.body.style.overflow = 'hidden'; }
  function closeMenu() { menu.classList.remove('open'); overlay?.classList.remove('active'); document.body.style.overflow = ''; }
  toggleBtn.addEventListener('click', openMenu);
  closeBtn?.addEventListener('click', closeMenu);
  overlay?.addEventListener('click', closeMenu);
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeMenu(); });
})();

// ============================================================
// CART DRAWER
// ============================================================
class CartDrawer {
  constructor() {
    this.drawer = $('.cart-drawer');
    this.overlay = $('#overlay');
    this.cartCount = $$('.cart-count');
    this.cartItems = $('.cart-drawer__items');
    this.cartFooter = $('.cart-drawer__footer');
    this.cartEmpty = $('.cart-drawer__empty');
    this.subtotalEl = $('.cart-subtotal__amount');
    if (!this.drawer) return;
    this.bindEvents();
    this.refresh();
  }
  bindEvents() {
    $$('[data-cart-toggle]').forEach(btn => { btn.addEventListener('click', () => this.toggle()); });
    $('.cart-drawer__close')?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
  }
  open() { this.drawer.classList.add('open'); this.overlay?.classList.add('active'); document.body.style.overflow = 'hidden'; this.refresh(); }
  close() { this.drawer.classList.remove('open'); this.overlay?.classList.remove('active'); document.body.style.overflow = ''; }
  toggle() { this.drawer.classList.contains('open') ? this.close() : this.open(); }
  async refresh() {
    try { const res = await fetch('/cart.js'); const cart = await res.json(); this.render(cart); } catch(e) { console.error('Cart refresh failed:', e); }
  }
  updateCount(count) { this.cartCount.forEach(el => { el.textContent = count; el.style.display = count > 0 ? 'flex' : 'none'; }); }
  render(cart) {
    this.updateCount(cart.item_count);
    const isEmpty = cart.items.length === 0;
    this.cartEmpty && (this.cartEmpty.style.display = isEmpty ? 'flex' : 'none');
    this.cartFooter && (this.cartFooter.style.display = isEmpty ? 'none' : 'block');
    if (this.cartItems) {
      if (isEmpty) { this.cartItems.innerHTML = ''; }
      else { this.cartItems.innerHTML = cart.items.map(item => this.renderItem(item)).join(''); this.bindItemEvents(); }
    }
    if (this.subtotalEl) this.subtotalEl.textContent = formatMoney(cart.total_price);
    this.renderUpsell(cart.total_price);
  }
  renderUpsell(totalPrice) {
    const upsellEl = $('.cart-upsell');
    if (!upsellEl) return;
    const threshold = 7500;
    const remaining = threshold - totalPrice;
    upsellEl.innerHTML = remaining <= 0 ? `You've unlocked <strong>FREE shipping!</strong>` : `Spend <strong>${formatMoney(remaining)}</strong> more for <strong>FREE shipping</strong>`;
  }
  renderItem(item) {
    return `<div class="cart-item" data-key="${item.key}"><img class="cart-item__img" src="${item.image}" alt="${item.title}" loading="lazy"><div class="cart-item__info"><p class="cart-item__title">${item.product_title}</p>${item.variant_title ? `<p class="cart-item__variant">${item.variant_title}</p>` : ''}<div class="cart-item__qty" style="margin-top:8px"><button class="cart-item__qty-btn" data-action="decrease" data-key="${item.key}" data-qty="${item.quantity}">−</button><span class="cart-item__qty-num">${item.quantity}</span><button class="cart-item__qty-btn" data-action="increase" data-key="${item.key}" data-qty="${item.quantity}">+</button></div></div><div style="text-align:right;display:flex;flex-direction:column;align-items:flex-end;gap:8px"><p class="cart-item__price">${formatMoney(item.final_line_price)}</p><button class="cart-item__remove" data-key="${item.key}" aria-label="Remove item"><svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 1L13 13M13 1L1 13" stroke="currentColor" stroke-width="1.5"/></svg></button></div></div>`;
  }
  bindItemEvents() {
    $$('.cart-item__qty-btn').forEach(btn => {
      btn.addEventListener('click', async () => {
        const key = btn.dataset.key;
        const action = btn.dataset.action;
        const currentQty = parseInt(btn.dataset.qty);
        const newQty = action === 'increase' ? currentQty + 1 : Math.max(0, currentQty - 1);
        await this.updateItem(key, newQty);
      });
    });
    $$('.cart-item__remove').forEach(btn => { btn.addEventListener('click', async () => { await this.updateItem(btn.dataset.key, 0); }); });
  }
  async updateItem(key, quantity) {
    try { const res = await fetch('/cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: key, quantity }) }); const cart = await res.json(); this.render(cart); } catch(e) { console.error('Update cart failed:', e); }
  }
  async addItem(variantId, quantity = 1, properties = {}) {
    try { const res = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: variantId, quantity, properties }) }); const data = await res.json(); if (data.status) throw new Error(data.description); await this.refresh(); this.open(); return data; } catch(e) { console.error('Add to cart failed:', e); throw e; }
  }
}
const cartDrawer = new CartDrawer();
window.cartDrawer = cartDrawer;

// ============================================================
// PRODUCT FORM — ADD TO CART
// ============================================================
(function initProductForms() {
  $$('.product-form').forEach(form => {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = form.querySelector('[type="submit"][name="add"]');
      const variantInput = form.querySelector('[name="id"]');
      if (!variantInput) return;
      const variantId = variantInput.value;
      const qtyInput = form.querySelector('[name="quantity"]');
      const quantity = qtyInput ? parseInt(qtyInput.value) : 1;
      btn.classList.add('loading');
      btn.innerHTML = '<span class="loading-spinner"></span>';
      try { await window.cartDrawer.addItem(variantId, quantity); }
      catch (err) { btn.textContent = 'Out of stock'; }
      finally { setTimeout(() => { btn.classList.remove('loading'); btn.textContent = 'Add to cart'; }, 800); }
    });
  });
})();

// ============================================================
// VARIANT SELECTOR — reads embedded JSON for live price updates
// ============================================================
(function initVariantSelector() {
  const variantJsonEl = document.getElementById('product-variants-json');
  if (!variantJsonEl) return;

  let variants = [];
  try { variants = JSON.parse(variantJsonEl.textContent); } catch(e) { return; }

  const productInfoEl = document.querySelector('.product-info');
  const form = document.querySelector('.product-form');
  if (!form || !productInfoEl) return;

  const hiddenInput = form.querySelector('[name="id"]');
  const priceGroup = productInfoEl.querySelector('.product-price-group');
  const allSelectors = $$('.variant-selector', form);

  function getSelectedOptions() {
    return allSelectors.map(sel => {
      const active = sel.querySelector('.variant-btn.active');
      return active ? active.dataset.value : null;
    });
  }

  function findVariant(opts) {
    return variants.find(v => {
      const vOpts = [v.option1, v.option2, v.option3];
      return opts.every((val, i) => !val || val === vOpts[i]);
    });
  }

  function updateUI(variant) {
    if (!variant) return;

    if (hiddenInput) hiddenInput.value = variant.id;

    const priceEl = priceGroup && priceGroup.querySelector('.product-price');
    if (priceEl) {
      priceEl.textContent = formatMoney(variant.price);
      priceEl.classList.toggle('product-price--sale', variant.compare_at_price > variant.price);
    }

    let compareEl = priceGroup && priceGroup.querySelector('.product-price--compare');
    let saveEl = priceGroup && priceGroup.querySelector('.product-save-badge');

    if (variant.compare_at_price > variant.price) {
      if (!compareEl && priceGroup) { compareEl = document.createElement('span'); compareEl.className = 'product-price--compare'; priceGroup.appendChild(compareEl); }
      if (!saveEl && priceGroup) { saveEl = document.createElement('span'); saveEl.className = 'product-save-badge'; priceGroup.appendChild(saveEl); }
      if (compareEl) compareEl.textContent = formatMoney(variant.compare_at_price);
      if (saveEl) { const pct = Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price); saveEl.textContent = 'Save ' + pct + '%'; }
    } else {
      if (compareEl) compareEl.remove();
      if (saveEl) saveEl.remove();
    }

    const stockEl = productInfoEl.querySelector('.product-stock');
    if (stockEl) {
      const qty = variant.inventory_quantity ?? (variant.available ? 999 : 0);
      if (qty > 10) stockEl.innerHTML = '<span class="stock-dot stock-dot--in"></span><span class="stock-in">In stock</span>';
      else if (qty > 0) stockEl.innerHTML = '<span class="stock-dot stock-dot--low"></span><span class="stock-low">Only ' + qty + ' left</span>';
      else stockEl.innerHTML = '<span class="stock-dot stock-dot--out"></span><span class="stock-out">Out of stock</span>';
    }

    const addBtn = form.querySelector('[type="submit"][name="add"]');
    if (addBtn) { addBtn.disabled = !variant.available; addBtn.textContent = variant.available ? 'Add to cart' : 'Sold out'; }

    if (variant.featured_image) {
      const gallery = document.querySelector('.product-gallery');
      if (gallery) {
        const mainImg = gallery.querySelector('.product-gallery__main img');
        const thumbs = gallery.querySelectorAll('.product-gallery__thumb');
        if (mainImg) mainImg.src = variant.featured_image.src;
        thumbs.forEach(t => t.classList.toggle('active', t.dataset.src === variant.featured_image.src));
      }
    }
  }

  // Expose reset function for bfcache
  window._volturaVariantReset = function() {
    const opts = getSelectedOptions();
    const variant = findVariant(opts);
    if (variant) updateUI(variant);
  };
  window._volturaVariantReset();

  allSelectors.forEach(selector => {
    const btns = $$('.variant-btn', selector);
    btns.forEach(btn => {
      btn.addEventListener('click', () => {
        btns.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        const labelSpan = selector.previousElementSibling && selector.previousElementSibling.querySelector('span');
        if (labelSpan) labelSpan.textContent = btn.dataset.value;
        const opts = getSelectedOptions();
        const variant = findVariant(opts);
        updateUI(variant);
      });
    });
  });
})();

// ============================================================
// PRODUCT GALLERY — THUMBNAIL SWITCH
// ============================================================
(function initProductGallery() {
  const gallery = $('.product-gallery');
  if (!gallery) return;
  const mainImg = $('.product-gallery__main img', gallery);
  const thumbs = $$('.product-gallery__thumb', gallery);
  thumbs.forEach(thumb => {
    thumb.addEventListener('click', () => {
      thumbs.forEach(t => t.classList.remove('active'));
      thumb.classList.add('active');
      if (mainImg) mainImg.src = thumb.dataset.src;
    });
  });
})();

// ============================================================
// QUANTITY STEPPER
// ============================================================
(function initQuantitySteppers() {
  $$('.quantity-input').forEach(stepper => {
    const input = stepper.querySelector('.quantity-input__field');
    const incBtn = stepper.querySelector('[data-direction="increase"]');
    const decBtn = stepper.querySelector('[data-direction="decrease"]');
    if (!input) return;
    incBtn?.addEventListener('click', () => { input.value = parseInt(input.value || 1) + 1; });
    decBtn?.addEventListener('click', () => { const c = parseInt(input.value || 1); if (c > 1) input.value = c - 1; });
  });
})();

// ============================================================
// COLLECTION — FILTER & SORT
// ============================================================
(function initCollectionFilter() {
  const sortSelect = $('#sort-select');
  if (sortSelect) { sortSelect.addEventListener('change', () => { const url = new URL(window.location); url.searchParams.set('sort_by', sortSelect.value); window.location = url.toString(); }); }
  $$('.filter-select').forEach(sel => {
    sel.addEventListener('change', () => {
      const url = new URL(window.location);
      if (sel.value) url.searchParams.set(sel.dataset.filterParam, sel.value);
      else url.searchParams.delete(sel.dataset.filterParam);
      window.location = url.toString();
    });
  });
})();

// ============================================================
// QUICK ADD
// ============================================================
(function initQuickAdd() {
  $$('[data-quick-add]').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      const variantId = btn.dataset.variantId;
      if (!variantId) return;
      btn.classList.add('loading');
      btn.innerHTML = '<span class="loading-spinner"></span>';
      try { await window.cartDrawer.addItem(variantId, 1); btn.innerHTML = '✓ Added'; setTimeout(() => { btn.classList.remove('loading'); btn.innerHTML = 'Add to cart'; }, 1500); }
      catch(e) { btn.innerHTML = 'Out of stock'; btn.classList.remove('loading'); }
    });
  });
})();

// ============================================================
// LIVE SEARCH
// ============================================================
(function initLiveSearch() {
  const searchInput = $('.header__search-input');
  if (!searchInput) return;
  const resultsContainer = document.createElement('div');
  resultsContainer.className = 'search-results-popup';
  resultsContainer.style.cssText = 'position:absolute;top:calc(100% + 8px);left:0;right:0;background:var(--bg-card);border:1px solid var(--border);border-radius:var(--radius-lg);overflow:hidden;box-shadow:var(--shadow-lg);display:none;z-index:999;';
  searchInput.closest('.header__search-form')?.appendChild(resultsContainer);
  searchInput.closest('.header__search-form').style.position = 'relative';
  const doSearch = debounce(async (query) => {
    if (query.length < 2) { resultsContainer.style.display = 'none'; return; }
    try {
      const res = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=5`);
      const data = await res.json();
      const products = data.resources?.results?.products || [];
      if (!products.length) { resultsContainer.style.display = 'none'; return; }
      resultsContainer.innerHTML = products.map(p => `<a href="${p.url}" style="display:flex;align-items:center;gap:12px;padding:12px 16px;border-bottom:1px solid var(--border);transition:background .15s" onmouseover="this.style.background='var(--bg-card-hover)'" onmouseout="this.style.background='transparent'"><img src="${p.image}" alt="${p.title}" style="width:44px;height:44px;border-radius:6px;object-fit:cover"><div><p style="font-size:14px;font-weight:600;color:var(--text-primary)">${p.title}</p><p style="font-size:13px;color:var(--text-muted)">${p.price}</p></div></a>`).join('') + `<a href="/search?q=${encodeURIComponent(query)}" style="display:block;padding:12px 16px;font-size:13px;color:var(--accent);font-weight:600;text-align:center">View all results →</a>`;
      resultsContainer.style.display = 'block';
    } catch(e) {}
  }, 300);
  searchInput.addEventListener('input', (e) => doSearch(e.target.value));
  document.addEventListener('click', (e) => { if (!e.target.closest('.header__search-form')) resultsContainer.style.display = 'none'; });
})();

// ============================================================
// SCROLL TO TOP
// ============================================================
(function initScrollTop() {
  const btn = $('#scroll-top');
  if (!btn) return;
  window.addEventListener('scroll', debounce(() => { btn.style.opacity = window.scrollY > 400 ? '1' : '0'; btn.style.pointerEvents = window.scrollY > 400 ? 'auto' : 'none'; }, 100), { passive: true });
  btn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
})();

// ============================================================
// TABS
// ============================================================
(function initTabs() {
  $$('.tab-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const parent = btn.closest('.featured-collection__tabs');
      $$('.tab-btn', parent).forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      const targetId = btn.dataset.tab;
      $$('.tab-panel').forEach(panel => { panel.style.display = panel.id === targetId ? 'grid' : 'none'; });
    });
  });
})();

// ============================================================
// SCROLL ANIMATIONS
// ============================================================
(function initScrollAnimations() {
  const style = document.createElement('style');
  style.textContent = '.animate-in{opacity:0;transform:translateY(24px);transition:opacity 0.5s ease,transform 0.5s ease}.animate-in.visible{opacity:1;transform:translateY(0)}';
  document.head.appendChild(style);
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry, i) => { if (entry.isIntersecting) { setTimeout(() => entry.target.classList.add('visible'), i * 80); observer.unobserve(entry.target); } });
  }, { threshold: 0.1 });
  $$('.product-card, .feature-card, .testimonial-card, .trust-badge').forEach(el => { el.classList.add('animate-in'); observer.observe(el); });
})();

// ============================================================
// BFCACHE — reset price when page restored from browser cache
// ============================================================
window.addEventListener('pageshow', function(e) {
  if (e.persisted && typeof window._volturaVariantReset === 'function') {
    window._volturaVariantReset();
  }
});
